import { searchByPostal } from '@/features/heartrails/searchByPostal';
import {
  api_portalOfficeHospitalListing_requestSearchPortalOfficeHospitalListingsUseEngine,
  service_RequestFilter,
  service_RequestLocation,
} from '@/init/swagger';
import {
  getOrderBySort,
  HOSPITAL_LIMIT,
  MOVING_MINUTES_TYPE,
  STATION_ADVANCED_SEARCH_TYPES,
} from '@/types/types';
import { HospitalListingOutput } from '../parseHospitalListings';
import { SearchHospitalsType } from '@/schemas/SearchParamsSchemas/app/stations/search/hospitals/Schema';
import { ParsedCommonStationsSearchPageSearchParams } from '@/schemas/SearchParamsSchemas/common/StationSearch/Schema';

export const buildRequestBody = ({
  validatedSearchParams,
  limit,
  offset,
}: {
  validatedSearchParams: ParsedCommonStationsSearchPageSearchParams;
  limit: number;
  offset: number;
}): api_portalOfficeHospitalListing_requestSearchPortalOfficeHospitalListingsUseEngine => {
  const sort = validatedSearchParams.sort;
  const order = getOrderBySort(sort);

  return {
    filters: mapSearchFilters({
      validatedSearchParams,
    }),
    limit,
    location: mapRequestBodyLocation({ validatedSearchParams }),
    medical_subjects: validatedSearchParams['medicalSubjects'],
    reported_additional_fees: validatedSearchParams['reportedAdditionalFees'],
    offset,
    sorts: [{ target: sort, order: order }],
    portal_patient_id: validatedSearchParams.patient_id,
    ...(validatedSearchParams.partnerName
      ? { partner_name: validatedSearchParams.partnerName }
      : {}),
  };
};

export const buildFormValues = ({
  validatedSearchParams,
}: {
  validatedSearchParams: ParsedCommonStationsSearchPageSearchParams;
}): SearchHospitalsType => {
  return {
    searchType: validatedSearchParams.searchType,
    location: {
      ...mapLocation({ validatedSearchParams }),
      moving_minutes: validatedSearchParams.movingMinutes,
      pref_name: validatedSearchParams.prefName,
    },
    filters: mapSearchFilters({
      validatedSearchParams,
    }),
    medical_subjects: validatedSearchParams['medicalSubjects'],
    reported_additional_fees: validatedSearchParams['reportedAdditionalFees'],
    sort: validatedSearchParams.sort,
    page: validatedSearchParams.page,
    patient_id: validatedSearchParams.patient_id,
    partnerName: validatedSearchParams.partnerName,
  };
};

export const buildSearchParamsString = ({
  formValues,
}: {
  formValues: SearchHospitalsType;
}) => {
  const searchParams = {
    searchType: formValues.searchType ?? 'zipcode',
    searchTarget: 'hospital',
    postCode: formValues.location.zipcode ?? '',
    address: formValues.location.address ?? '',
    movingMinutes: formValues.location.moving_minutes ?? '',
    searchFilters:
      formValues.filters?.map(
        (f) => `${f.category_id}_${f.search_filter_id}`,
      ) ?? [],
    medicalSubjects: formValues.medical_subjects ?? [],
    reportedAdditionalFees: formValues.reported_additional_fees ?? [],
    sort: formValues.sort ?? 'updated_at',
    page: formValues.page ?? 1,
    prefName: formValues.location.pref_name ?? '北海道',
    patient_id: formValues.patient_id ?? NaN,
    partnerName: formValues.partnerName ?? '',
  };

  const newQuery = new URLSearchParams();

  newQuery.set('searchTarget', 'hospital');
  newQuery.set('searchType', searchParams.searchType);
  newQuery.set('movingMinutes', searchParams.movingMinutes);
  newQuery.set('sort', searchParams.sort);
  newQuery.set('page', searchParams.page.toString());
  newQuery.set('patient_id', searchParams.patient_id.toString());

  if (searchParams.searchType === STATION_ADVANCED_SEARCH_TYPES.ZIPCODE) {
    newQuery.set('postCode', searchParams.postCode);
    newQuery.delete('prefName');
    newQuery.delete('address');
    newQuery.delete('partnerName');
  } else if (
    searchParams.searchType === STATION_ADVANCED_SEARCH_TYPES.ADDRESS
  ) {
    newQuery.set('prefName', searchParams.prefName);
    newQuery.set('address', searchParams.address);
    newQuery.delete('postCode');
    newQuery.delete('partnerName');
  } else if (
    searchParams.searchType === STATION_ADVANCED_SEARCH_TYPES.PARTNER
  ) {
    newQuery.set('prefName', searchParams.prefName);
    newQuery.set('partnerName', searchParams.partnerName);
    newQuery.delete('postCode');
    newQuery.delete('address');
  }

  searchParams.searchFilters.forEach((filter) => {
    newQuery.append('searchFilters[]', filter);
  });
  searchParams.medicalSubjects.forEach((medicalSubject) => {
    newQuery.append('medicalSubjects', medicalSubject.toString());
  });
  searchParams.reportedAdditionalFees.forEach((reportedAdditionalFee) => {
    newQuery.append('reportedAdditionalFees', reportedAdditionalFee.toString());
  });

  return newQuery.toString();
};

export const buildTargetAddress = async ({
  validatedSearchParams,
}: {
  validatedSearchParams: ParsedCommonStationsSearchPageSearchParams;
}): Promise<string> => {
  if (
    validatedSearchParams.searchType === STATION_ADVANCED_SEARCH_TYPES.ZIPCODE
  ) {
    const data = await searchByPostal({
      postal: validatedSearchParams.postCode || '',
    });
    if (data?.length) {
      const geo = data[0];
      let autoTargetAddressTextByPostCode = '';
      const prefecture = geo.prefecture;
      const city = geo.city;
      const town = geo.town;

      if (prefecture) {
        autoTargetAddressTextByPostCode += prefecture;
      }
      if (city) {
        autoTargetAddressTextByPostCode += city;
      }
      if (town) {
        autoTargetAddressTextByPostCode += town;
      }

      return autoTargetAddressTextByPostCode;
    } else {
      return '';
    }
  } else if (
    validatedSearchParams.searchType === STATION_ADVANCED_SEARCH_TYPES.ADDRESS
  ) {
    let autoTargetAddressTextByPostCode = '';
    const prefName = validatedSearchParams.prefName;
    const address = validatedSearchParams.address;
    if (prefName) {
      autoTargetAddressTextByPostCode += prefName;
    }
    if (address) {
      autoTargetAddressTextByPostCode += address;
    }
    return autoTargetAddressTextByPostCode;
  } else if (
    validatedSearchParams.searchType === STATION_ADVANCED_SEARCH_TYPES.PARTNER
  ) {
    return validatedSearchParams.prefName || '';
  }
  return '';
};

export const buildPaginationInfo = ({
  validatedSearchParams,
  hospitalListings,
  totalCount,
}: {
  validatedSearchParams: ParsedCommonStationsSearchPageSearchParams;
  hospitalListings: HospitalListingOutput[];
  totalCount: number;
}): PaginationInfo => {
  const page = validatedSearchParams.page;
  const lastPage = Math.ceil(totalCount / HOSPITAL_LIMIT);

  return {
    current: {
      from: (page - 1) * HOSPITAL_LIMIT + 1,
      to: Math.min(page * HOSPITAL_LIMIT, totalCount),
      page,
    },
    pages: Array.from({ length: lastPage }, (_, i) => i + 1),
    lastPage,
    allListingTotalCount: totalCount,
    displayListingCount: hospitalListings.length,
    isShowPagination: totalCount > HOSPITAL_LIMIT,
  };
};

const mapSearchFilters = ({
  validatedSearchParams,
}: {
  validatedSearchParams: ParsedCommonStationsSearchPageSearchParams;
}): Array<service_RequestFilter> => {
  return (
    validatedSearchParams['searchFilters[]'].reduce<service_RequestFilter[]>(
      (acc, filter) => {
        const categoryId = Number(filter.category_id);
        const filterId = Number(filter.search_filter_id);

        if (!isNaN(categoryId) && !isNaN(filterId)) {
          acc.push({ category_id: categoryId, search_filter_id: filterId });
        }
        return acc;
      },
      [],
    ) || []
  );
};

const mapLocation = ({
  validatedSearchParams,
}: {
  validatedSearchParams: ParsedCommonStationsSearchPageSearchParams;
}): service_RequestLocation => {
  const locationBody: service_RequestLocation = {
    address: validatedSearchParams.address,
    zipcode: validatedSearchParams.postCode,
  };

  // NOTE: アンダースコアの直後が数字のみで終わる場合に分割
  const [movingType, minutes] =
    validatedSearchParams.movingMinutes.split(/_(?=\d+$)/);
  if (movingType && minutes && isMovingMinutesType(movingType)) {
    const minutesNumber = Number(minutes);
    if (!isNaN(minutesNumber)) {
      locationBody[movingType] = minutesNumber;
    }
  }

  return locationBody;
};

const mapRequestBodyLocation = ({
  validatedSearchParams,
}: {
  validatedSearchParams: ParsedCommonStationsSearchPageSearchParams;
}): service_RequestLocation => {
  const locationBody: service_RequestLocation = {};

  const searchType = validatedSearchParams.searchType;
  if (searchType === STATION_ADVANCED_SEARCH_TYPES.ZIPCODE) {
    locationBody.zipcode = validatedSearchParams.postCode;
  } else if (searchType === STATION_ADVANCED_SEARCH_TYPES.ADDRESS) {
    locationBody.address = [
      validatedSearchParams.prefName,
      validatedSearchParams.address,
    ].join('');
  } else if (searchType === STATION_ADVANCED_SEARCH_TYPES.PARTNER) {
    if (validatedSearchParams.prefName) {
      locationBody.address = validatedSearchParams.prefName;
    }
  }

  // NOTE: アンダースコアの直後が数字のみで終わる場合に分割
  const [movingType, minutes] =
    validatedSearchParams.movingMinutes.split(/_(?=\d+$)/);
  if (movingType && minutes && isMovingMinutesType(movingType)) {
    const minutesNumber = Number(minutes);
    if (!isNaN(minutesNumber)) {
      locationBody[movingType] = minutesNumber;
    }
  }

  return locationBody;
};

const isMovingMinutesType = (value: string): value is MOVING_MINUTES_TYPE => {
  const MOVING_MINUTES_SET = new Set<MOVING_MINUTES_TYPE>(
    Object.values(MOVING_MINUTES_TYPE),
  );
  return MOVING_MINUTES_SET.has(value as MOVING_MINUTES_TYPE);
};

export type PaginationInfo = {
  current: {
    from: number;
    to: number;
    page: number;
  };
  pages: number[];
  lastPage: number;
  allListingTotalCount: number | undefined;
  displayListingCount: number | undefined;
  isShowPagination: boolean;
};
